import React from 'react';
import styles from './CustomSettings.module.scss';
import { CustomSettingKey } from '../../Base/Enums/CustomSettingKey.enum';
import CustomSettingsItem, {
	CustomSettingsItemScheme,
} from './CustomSettingsItem';
import useLoading from '../../Base/Hooks/useLoading';
import useCustomSettings from '../../Base/Hooks/useCustomSettings';
import { useAppContext } from '../../../Services/AppContext';
import useNotification from '../../../Hooks/useNotification.hook';
import { useEffectAsync } from '../../../helpers';
import { useForm } from 'react-hook-form';
import FormWrapper from '../../../Components/Form/FormWrapper';
import CustomSettingsTable from './CustomSettingsTable/CustomSettingsTable';
import SubmitButton from '../../../Components/Button/SubmitButton';
import useCustomSettingsForm from '../Hooks/useCustomSettingsForm';
import { yupResolver } from '@hookform/resolvers/yup';

interface Props {
	storageKey: CustomSettingKey;
	items: CustomSettingsItemScheme[];
	onSave: () => void;
}

const CustomSettings: React.FC<Props> = ({ storageKey, items, onSave }) => {
	const { setIsLoading, isLoading, RenderLoading } = useLoading(true);
	const { workplaceId, userData } = useAppContext();
	const { getCustomSettings, saveCustomSettings } = useCustomSettings(
		workplaceId,
		userData.id,
	);
	const { showNotificationSuccess } = useNotification();
	const { validationScheme, defaultValues } = useCustomSettingsForm(items);

	const formMethods = useForm({
		resolver: yupResolver(validationScheme),
		defaultValues,
	});

	const handleLoad = async () => {
		setIsLoading(true);
		const data = await getCustomSettings(storageKey);
		if (data) {
			formMethods.reset(data);
		}
		setIsLoading(false);
	};

	useEffectAsync(async () => {
		await handleLoad();
	}, []);

	const handleSubmit = async (values: Record<string, any>) => {
		setIsLoading(true);
		const response = await saveCustomSettings(storageKey, values);
		if (response?.status === 200) {
			showNotificationSuccess({ content: 'base.saved' });
			await handleLoad();
			onSave();
		}
		setIsLoading(false);
	};

	if (isLoading) {
		return <RenderLoading />;
	}

	return (
		<div className={styles.wrapper}>
			<FormWrapper formMethods={formMethods}>
				<CustomSettingsTable className={styles.table}>
					{items.map((item, key) => (
						<CustomSettingsItem item={item} key={key} />
					))}
				</CustomSettingsTable>
				<div className='my-3 px-3'>
					<SubmitButton onSubmit={handleSubmit} title='base.shared.save' />
				</div>
			</FormWrapper>
		</div>
	);
};

export default CustomSettings;
